import { useGlobalStore } from '@common/core-layer/stores/global.store'

import type {
  IChatParticipantIds,
  IConversation,
  IConversationApi,
  IConversationRecipient,
  IConversationRecipientApi,
  IConversationSuggestion,
  IConversationsResponse,
} from '../types/conversation.types'
import type { ConversationEventPayload } from '../types/socket.types'

/** Current app mode for chats (GoKollab vs UserV2). */
export function getChatsPlatformIsGokollab(): boolean {
  return useGlobalStore().isGokollab
}

/**
 * Single platform chat id from raw contactId/userId.
 * GoKollab → contactId. UserV2 (clientportal) → userId.
 */
export function getChatParticipantId(participant: IChatParticipantIds): string {
  const contactId = participant.contactId?.trim() || ''
  const userId = participant.userId?.trim() || ''
  const isGokollab = getChatsPlatformIsGokollab()

  if (isGokollab) {
    return contactId || userId
  }

  return userId || contactId
}

/**
 * Platform chat id for the user who triggered a conversation event (typing, etc.).
 * Socket payloads use legacy field names: GoKollab favors contactId; UserV2 favors sender (userId).
 */
export function getConversationEventSenderId(payload: Pick<ConversationEventPayload, 'contactId' | 'sender'>): string {
  const contactId = payload.contactId?.trim() || ''
  const sender = payload.sender?.trim() || ''

  if (getChatsPlatformIsGokollab()) {
    return contactId || sender
  }

  return sender || contactId
}

export function suggestionToRecipientApi(suggestion: IConversationSuggestion): IConversationRecipientApi {
  return {
    contactId: suggestion.contactId,
    userId: suggestion.userId,
    fullName: suggestion.fullName,
    avatar: suggestion.avatar,
    slug: suggestion.slug,
    bio: suggestion.bio,
  }
}

/**
 * Maps API recipient to app recipient: sets recipientId only (no contactId/userId).
 */
export function normalizeConversationRecipient(recipient: IConversationRecipientApi): IConversationRecipient {
  return {
    recipientId: getChatParticipantId(recipient),
    fullName: recipient.fullName,
    avatar: recipient.avatar,
    slug: recipient.slug,
    bio: recipient.bio,
    isOnline: recipient.isOnline,
  }
}

export function normalizeConversation(conversation: IConversationApi): IConversation {
  return {
    ...conversation,
    recepients: (conversation.recepients ?? []).map(recipient => normalizeConversationRecipient(recipient)),
  }
}

export function normalizeConversationsResponse(response: {
  conversations: IConversationApi[]
  hasMore: boolean
  nextCursor?: string
}): IConversationsResponse {
  return {
    ...response,
    conversations: (response.conversations ?? []).map(conversation => normalizeConversation(conversation)),
  }
}
